import React, { useRef } from 'react';
import cn from 'classnames';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import Alert, { IAlertProps } from './Alert';
import { AlertPlacement } from './AlertContainer';
import styles from './alerts.module.scss';

export interface IAlertsProps extends IAlertProps {
  id?: string,
}

export interface IProps {
  alerts: IAlertsProps[],
  onHide?: (alert: IAlertsProps) => void,
  enterTimeout?: number,
  exitTimeout?: number,
  containerClassName?: string,
  alertClassName?: string,
  placement?: AlertPlacement,
  bounceId?: string | null,
}

const Alerts = ({
  alerts,
  onHide = () => {},
  enterTimeout = 300,
  exitTimeout = 300,
  containerClassName: propsContainerClassName,
  alertClassName: propsAlertClassName,
  placement = 'topCenter',
  bounceId = null,
}: IProps) => {
  const containerClassName = cn(
    styles.container,
    styles[placement],
    propsContainerClassName,
    { 'is-empty': !alerts.length },
  );

  const alertClassName = propsAlertClassName || styles.alert;

  const handleHide = (alert: IAlertsProps) => {
    if (onHide) {
      onHide(alert);
    }
  };

  const nodeRefs = useRef(new Map<string, React.RefObject<HTMLDivElement>>());

  return (
    <div className={ containerClassName }>
      <TransitionGroup>
        { alerts.map((alert) => {
          const key = alert.id || String(new Date().getTime());

          if (!nodeRefs.current.has(key)) {
            nodeRefs.current.set(key, React.createRef<HTMLDivElement>());
          }

          return (
            <CSSTransition
              key={ key }
              timeout={ {
                enter: enterTimeout,
                exit: exitTimeout,
              } }
              classNames={ alertClassName }
              nodeRef={ nodeRefs.current.get(key) }
            >
              <Alert
                ref={ nodeRefs.current.get(key) }
                className={ alertClassName }
                type={ alert.type }
                title={ alert.title }
                message={ alert.message }
                timeOut={ alert.timeOut }
                onClick={ alert.onClick }
                onHide={ () => handleHide(alert) }
                icon={ alert.icon }
                closable={ alert.closable }
                onClose={ alert.onClose }
                action={ alert.action }
                secondaryAction={ alert.secondaryAction }
                isLoading={ alert.isLoading }
                bounce={ alert.bounce || bounceId === alert.id }
              />
            </CSSTransition>
          );
        }) }
      </TransitionGroup>
    </div>
  );
};

export default Alerts;
